import { useNavigate } from 'react-router-dom';
import {
  Building2,
  FolderKanban,
  Handshake,
  KanbanSquare,
  UserPlus,
  Users,
} from 'lucide-react';
import type { DigestDto } from '@epasu/core';
import type { PipelineMetaRow, ProjectRow } from '@/lib/crm-client';
import { Card } from '@/components/ui/card';
import { formatEuro } from '@/lib/utils';
import { Kachel } from './Kachel';

/**
 * Der Bestand: wie groß ist das, womit wir arbeiten.
 *
 * `organizations` zählt alle Firmen, Lead-Firmen eingeschlossen — deshalb
 * führt die Kachel auf `/firmen?status=alle`; ohne den Filter öffnete die
 * Firmenliste auf „Kunden“ und zeigte weniger, als die Kachel nennt.
 * `contacts` zählt nur Qualifizierte (Lebenszyklus `kontakt`), die kalt
 * angeschriebenen Leads bekommen daneben ihre eigene Kachel, weil die Zahl ja
 * interessant ist, nur eine andere. Eine Kontaktliste gibt es nicht mehr; die
 * Kontakte stehen in ihren Firmen.
 *
 * `kunden` sind die aktiven Kunden — dieselbe Menge wie `/firmen?status=kunde`.
 * Ruhende sind seit Task 10 ein eigener Firmenstatus (`?status=ruht`) und
 * zählen hier nicht mit.
 *
 * Die fälligen Freigaben stehen nicht hier, sondern als Zeile unter den
 * Kacheln „Was ansteht“: sie sind Arbeit, kein Bestand.
 *
 * Darunter die offenen Deals aufgeschlüsselt nach Pipeline — die Summe allein
 * sagt nicht, ob sie bei den Schulen oder bei den Fotografen liegen.
 */
export function StatsRow({
  digest,
  kunden,
  pipelines,
  projekte,
}: {
  digest: DigestDto;
  kunden: number;
  pipelines: PipelineMetaRow[];
  projekte: ProjectRow[];
}) {
  const navigate = useNavigate();
  const mitDeals = pipelines.filter((p) => p.dealCount > 0);

  // `projekte` enthaelt nur Laufendes — `projects.list` laesst Endzustaende
  // ohne `includeClosed` weg. Ueberfaellig heisst: Frist vor heute.
  const heute = new Date().toISOString().slice(0, 10);
  const ueberfaellig = projekte.filter((p) => p.dueDate && p.dueDate < heute).length;
  const wertInArbeit = projekte.reduce((n, p) => n + p.orderValueCents, 0);

  return (
    <section>
      <h2 className="mb-3 text-sm font-semibold">Bestand</h2>
      {/* Sechs Kacheln: ab `lg` zweimal drei, darunter zweispaltig. */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <Kachel
          label="Kontakte"
          value={digest.contacts}
          icon={Users}
          onClick={() => navigate('/firmen?status=alle')}
        />
        <Kachel
          label="Firmen"
          value={digest.organizations}
          icon={Building2}
          onClick={() => navigate('/firmen?status=alle')}
        />
        <Kachel
          label="Leads"
          value={digest.leads}
          icon={UserPlus}
          hinweis="kalt angeschrieben"
          onClick={() => navigate('/akquise?reiter=leads')}
        />
        <Kachel
          label="Kunden"
          value={kunden}
          icon={Handshake}
          betont={kunden > 0}
          hinweis="aktiv, mit Akte"
          onClick={() => navigate('/firmen?status=kunde')}
        />
        <Kachel
          label="Offene Deals"
          value={digest.openDeals}
          icon={KanbanSquare}
          onClick={() => navigate('/akquise')}
        />
        <Kachel
          label="Laufende Projekte"
          value={projekte.length}
          icon={FolderKanban}
          betont={projekte.length > 0}
          dringend={ueberfaellig > 0}
          hinweis={
            ueberfaellig > 0
              ? `${ueberfaellig} überfällig`
              : wertInArbeit > 0
                ? `${formatEuro(wertInArbeit)} beauftragt`
                : undefined
          }
          onClick={() => navigate('/projekte')}
        />
      </div>

      {mitDeals.length > 0 && (
        <Card className="mt-3 flex flex-wrap gap-x-6 gap-y-2 px-4 py-3">
          {mitDeals.map((p) => (
            <button
              key={p.id}
              onClick={() => navigate('/akquise')}
              className="text-left text-xs transition-colors hover:text-foreground"
            >
              <span className="text-muted-foreground">{p.name}</span>{' '}
              <span className="font-semibold">{p.dealCount}</span>
            </button>
          ))}
        </Card>
      )}
    </section>
  );
}
